/**
 * #439 Claymorphism 3D Effect (粘土拟物 3D 效果)
 * 
 * 来源：2026 Motion Graphics 趋势 - Hybrid 2D/3D "Claymorphism"
 * 
 * 技术特点：
 * - 柔和的 3D 渲染（Soft 3D rendering）
 * - 次表面散射效果（Subsurface scattering）
 * - 混合媒体合成（2D 矢量 + 3D 渲染）
 * - 环境光遮蔽和柔和阴影（Ambient occlusion + soft shadows）
 * - 非真实感渲染（Non-photorealistic rendering, NPR）
 * 
 * 视觉效果：
 * - 温暖、触感的粘土动画质感
 * - 亲和、友好的美学
 * - 有深度但不刺眼的真实感
 * 
 * 适用场景：
 * - Tech SaaS 产品动画
 * - 教育和解释性内容
 * - 品牌吉祥物和角色动画
 */

import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
  random,
} from 'remotion';

// 定义颜色主题
const COLORS = {
  // 粘土主题色（柔和、温暖）
  clay1: '#FFB5BA', // 淡粉色
  clay2: '#FFCBA4', // 桃色
  clay3: '#B5EAD7', // 薄荷绿
  clay4: '#C7CEEA', // 淡紫色
  clay5: '#FFDAC1', // 珊瑚色
  clay6: '#E2F0CB', // 淡绿色
  
  // 阴影和高光
  shadow: 'rgba(0, 0, 0, 0.15)',
  highlight: 'rgba(255, 255, 255, 0.4)',
  
  // 背景
  bgGradient: ['#FFF5F5', '#FFF0E6'],
};

// 粘土球体组件（带有柔和阴影和高光）
const ClaySphere: React.FC<{
  x: number;
  y: number;
  radius: number;
  color: string;
  delay: number;
  frame: number;
  fps: number;
}> = ({ x, y, radius, color, delay, frame, fps }) => {
  // Spring 物理动画
  const springConfig = { damping: 15, stiffness: 100, mass: 0.8 };
  const progress = spring({
    frame: frame - delay,
    fps,
    config: springConfig,
  });
  
  // 呼吸动画
  const breathe = Math.sin(frame * 0.05) * 5;
  
  // 轻微的旋转
  const rotation = interpolate(
    progress,
    [0, 1],
    [-30, 0]
  );
  
  // 缩放效果
  const scale = interpolate(progress, [0, 1], [0.5, 1]);
  
  return (
    <div
      style={{
        position: 'absolute',
        left: x - radius,
        top: y - radius,
        width: radius * 2,
        height: radius * 2,
        transform: `scale(${scale}) rotate(${rotation}deg)`,
        opacity: progress,
      }}
    >
      {/* 主体 */}
      <div
        style={{
          width: '100%',
          height: '100%',
          borderRadius: '50%',
          background: color,
          boxShadow: `
            0 ${20 + breathe * 0.5}px ${40 + breathe}px ${COLORS.shadow},
            inset -${radius * 0.2}px -${radius * 0.3}px ${radius * 0.4}px ${COLORS.shadow},
            inset ${radius * 0.15}px ${radius * 0.15}px ${radius * 0.3}px ${COLORS.highlight}
          `,
          filter: 'blur(0.5px)',
        }}
      />
      
      {/* 高光 */}
      <div
        style={{
          position: 'absolute',
          top: '15%',
          left: '20%',
          width: '30%',
          height: '30%',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${COLORS.highlight} 0%, transparent 70%)`,
          filter: 'blur(3px)',
        }}
      />
    </div>
  );
};

// 粘土立方体组件
const ClayCube: React.FC<{
  x: number;
  y: number;
  size: number;
  color: string;
  delay: number;
  frame: number;
  fps: number;
}> = ({ x, y, size, color, delay, frame, fps }) => {
  const springConfig = { damping: 12, stiffness: 80, mass: 1 };
  const progress = spring({
    frame: frame - delay,
    fps,
    config: springConfig,
  });
  
  const rotation = interpolate(progress, [0, 1], [-45, 0]);
  const scale = interpolate(progress, [0, 1], [0.6, 1]);
  
  // 浮动动画
  const float = Math.sin(frame * 0.03 + delay * 0.1) * 8;
  
  return (
    <div
      style={{
        position: 'absolute',
        left: x - size / 2,
        top: y - size / 2 + float,
        width: size,
        height: size,
        transform: `scale(${scale}) rotate(${rotation}deg)`,
        opacity: progress,
        perspective: '800px',
      }}
    >
      {/* 主体 */}
      <div
        style={{
          width: '100%',
          height: '100%',
          background: color,
          borderRadius: '20px',
          boxShadow: `
            0 25px 50px ${COLORS.shadow},
            inset -5px -8px 15px ${COLORS.shadow},
            inset 5px 5px 10px ${COLORS.highlight}
          `,
          filter: 'blur(0.3px)',
        }}
      />
      
      {/* 高光 */}
      <div
        style={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: '40%',
          height: '40%',
          borderRadius: '15px',
          background: `radial-gradient(circle, ${COLORS.highlight} 0%, transparent 70%)`,
          filter: 'blur(5px)',
        }}
      />
    </div>
  );
};

// 粘土圆柱体组件
const ClayCylinder: React.FC<{
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  delay: number;
  frame: number;
  fps: number;
}> = ({ x, y, width, height, color, delay, frame, fps }) => {
  const springConfig = { damping: 18, stiffness: 90 };
  const progress = spring({
    frame: frame - delay,
    fps,
    config: springConfig,
  });
  
  const rotation = interpolate(progress, [0, 1], [30, 0]);
  const scale = interpolate(progress, [0, 1], [0.7, 1]);
  
  // 轻微的摇摆
  const swing = Math.sin(frame * 0.04 + delay * 0.2) * 3;
  
  return (
    <div
      style={{
        position: 'absolute',
        left: x - width / 2,
        top: y - height / 2,
        width: width,
        height: height,
        transform: `scale(${scale}) rotate(${rotation + swing}deg)`,
        opacity: progress,
      }}
    >
      {/* 顶部椭圆 */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '30%',
          borderRadius: '50%',
          background: color,
          boxShadow: `
            inset -3px -3px 8px ${COLORS.shadow},
            inset 3px 3px 6px ${COLORS.highlight}
          `,
          filter: 'blur(0.3px)',
        }}
      />
      
      {/* 身体 */}
      <div
        style={{
          position: 'absolute',
          top: '15%',
          left: 0,
          width: '100%',
          height: '70%',
          background: `linear-gradient(to bottom, ${color}, ${color}dd)`,
          boxShadow: `
            0 20px 40px ${COLORS.shadow},
            inset -3px 0 8px ${COLORS.shadow},
            inset 3px 0 6px ${COLORS.highlight}
          `,
          filter: 'blur(0.3px)',
        }}
      />
    </div>
  );
};

// 装饰粒子
const DecorativeParticle: React.FC<{
  index: number;
  frame: number;
  width: number;
  height: number;
  fps: number;
}> = ({ index, frame, width, height, fps }) => {
  // 使用确定性随机
  const seed = index * 12345.6789;
  const x = ((seed * 1.1) % width);
  const y = ((seed * 1.3) % height);
  const size = 10 + (seed % 20);
  const delay = (seed % 30);
  
  const springConfig = { damping: 20, stiffness: 100 };
  const progress = spring({
    frame: frame - delay,
    fps,
    config: springConfig,
  });
  
  // 浮动动画
  const float = Math.sin(frame * 0.02 + seed) * 15;
  const floatX = Math.cos(frame * 0.015 + seed * 0.5) * 10;
  
  // 颜色循环
  const colors = Object.values(COLORS).slice(0, 6).filter((c): c is string => typeof c === 'string');
  const color = colors[Math.floor(seed) % colors.length];
  
  return (
    <div
      style={{
        position: 'absolute',
        left: x + floatX,
        top: y + float,
        width: size,
        height: size,
        borderRadius: '50%',
        background: color,
        boxShadow: `
          0 ${size * 0.2}px ${size * 0.4}px ${COLORS.shadow},
          inset -${size * 0.1}px -${size * 0.15}px ${size * 0.2}px ${COLORS.shadow},
          inset ${size * 0.1}px ${size * 0.1}px ${size * 0.15}px ${COLORS.highlight}
        `,
        opacity: progress * 0.6,
        transform: `scale(${progress})`,
        filter: 'blur(0.8px)',
      }}
    />
  );
};

// 主组件
export const Claymorphism3D: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();
  
  // 中心位置
  const centerX = width / 2;
  const centerY = height / 2;
  
  return (
    <AbsoluteFill
      style={{
        background: `radial-gradient(circle at ${centerX}px ${centerY}px, ${COLORS.bgGradient[0]}, ${COLORS.bgGradient[1]})`,
        overflow: 'hidden',
      }}
    >
      {/* 背景装饰粒子（30 个） */}
      {Array.from({ length: 30 }).map((_, i) => (
        <DecorativeParticle
          key={i}
          index={i}
          frame={frame}
          width={width}
          height={height}
          fps={fps}
        />
      ))}
      
      {/* 主要粘土形状 */}
      
      {/* 中心大球 */}
      <ClaySphere
        x={centerX}
        y={centerY}
        radius={120}
        color={COLORS.clay1}
        delay={0}
        frame={frame}
        fps={fps}
      />
      
      {/* 左上小球 */}
      <ClaySphere
        x={centerX - 200}
        y={centerY - 150}
        radius={80}
        color={COLORS.clay2}
        delay={10}
        frame={frame}
        fps={fps}
      />
      
      {/* 右下小球 */}
      <ClaySphere
        x={centerX + 220}
        y={centerY + 180}
        radius={90}
        color={COLORS.clay3}
        delay={15}
        frame={frame}
        fps={fps}
      />
      
      {/* 左下立方体 */}
      <ClayCube
        x={centerX - 180}
        y={centerY + 200}
        size={100}
        color={COLORS.clay4}
        delay={20}
        frame={frame}
        fps={fps}
      />
      
      {/* 右上立方体 */}
      <ClayCube
        x={centerX + 200}
        y={centerY - 180}
        size={80}
        color={COLORS.clay5}
        delay={25}
        frame={frame}
        fps={fps}
      />
      
      {/* 右侧圆柱 */}
      <ClayCylinder
        x={centerX + 250}
        y={centerY}
        width={70}
        height={140}
        color={COLORS.clay6}
        delay={30}
        frame={frame}
        fps={fps}
      />
      
      {/* 左侧圆柱 */}
      <ClayCylinder
        x={centerX - 240}
        y={centerY + 50}
        width={60}
        height={120}
        color={COLORS.clay2}
        delay={35}
        frame={frame}
        fps={fps}
      />
      
      {/* 中心小立方体 */}
      <ClayCube
        x={centerX + 100}
        y={centerY - 120}
        size={60}
        color={COLORS.clay3}
        delay={40}
        frame={frame}
        fps={fps}
      />
      
      {/* 底部小球 */}
      <ClaySphere
        x={centerX}
        y={centerY + 300}
        radius={70}
        color={COLORS.clay4}
        delay={45}
        frame={frame}
        fps={fps}
      />
      
      {/* 标题 */}
      <div
        style={{
          position: 'absolute',
          top: 60,
          left: '50%',
          transform: 'translateX(-50%)',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          fontSize: 64,
          fontWeight: 700,
          color: '#2d3748',
          textShadow: `
            0 4px 8px rgba(0, 0, 0, 0.1),
            0 2px 4px rgba(0, 0, 0, 0.06)
          `,
          opacity: spring({ frame: frame - 50, fps, config: { damping: 15 } }),
        }}
      >
        Claymorphism 3D
      </div>
      
      {/* 副标题 */}
      <div
        style={{
          position: 'absolute',
          top: 140,
          left: '50%',
          transform: 'translateX(-50%)',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          fontSize: 30,
          color: '#4a5568',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
          opacity: spring({ frame: frame - 55, fps, config: { damping: 15 } }),
        }}
      >
        柔和 · 触感 · 温暖
      </div>
      
      {/* 信息面板 */}
      <div
        style={{
          position: 'absolute',
          bottom: 50,
          left: 50,
          background: 'rgba(255, 255, 255, 0.6)',
          backdropFilter: 'blur(10px)',
          borderRadius: '20px',
          padding: '24px 32px',
          boxShadow: `
            0 10px 30px rgba(0, 0, 0, 0.1),
            inset -2px -3px 8px rgba(0, 0, 0, 0.05),
            inset 2px 2px 6px rgba(255, 255, 255, 0.5)
          `,
          fontFamily: 'system-ui, -apple-system, sans-serif',
          fontSize: 22,
          color: '#2d3748',
          opacity: spring({ frame: frame - 60, fps, config: { damping: 15 } }),
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 26 }}>
          #439 Claymorphism 3D Effect
        </div>
        <div style={{ opacity: 0.8, lineHeight: 1.6 }}>
          • 次表面散射效果<br/>
          • 环境光遮蔽 + 柔和阴影<br/>
          • 非真实感渲染（NPR）<br/>
          • 温暖的粘土质感
        </div>
      </div>
      
      {/* 统计信息 */}
      <div
        style={{
          position: 'absolute',
          bottom: 50,
          right: 50,
          background: 'rgba(255, 255, 255, 0.6)',
          backdropFilter: 'blur(10px)',
          borderRadius: '20px',
          padding: '24px 32px',
          boxShadow: `
            0 10px 30px rgba(0, 0, 0, 0.1),
            inset -2px -3px 8px rgba(0, 0, 0, 0.05),
            inset 2px 2px 6px rgba(255, 255, 255, 0.5)
          `,
          fontFamily: 'system-ui, -apple-system, sans-serif',
          fontSize: 22,
          color: '#2d3748',
          opacity: spring({ frame: frame - 65, fps, config: { damping: 15 } }),
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 12, fontSize: 26 }}>
          技术指标
        </div>
        <div style={{ opacity: 0.8, lineHeight: 1.6 }}>
          • 3 种形状：球体、立方体、圆柱<br/>
          • 6 种柔和色调<br/>
          • 30 个装饰粒子<br/>
          • Spring 物理动画
        </div>
      </div>
    </AbsoluteFill>
  );
};
